import { useMemo, useState } from 'react';
import {
	Alert,
	AutoComplete,
	Button,
	Flex,
	Popconfirm,
	Select,
	Table,
	Typography,
	notification,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';

import type { ProductionMethodKeyence } from '../../../../interfaces/Ballooning';
import {
	useCreateKeyenceByProductionMethodMutation,
	useDeleteKeyenceByProductionMethodMutation,
	useGetKeyenceByProductionMethodQuery,
	useGetProductionMethodsQuery,
	useUpdateKeyenceByProductionMethodMutation,
} from '../../../../redux/api/ballooning';
import { useGetKeyenceSystemsQuery } from '../../../../redux/api/quality';
import { extractApiError } from '../../../../utils/apiError';

const { Paragraph, Title } = Typography;

/**
 * Which Keyence device measures each production method (Fastlane -> IM-X.1).
 *
 * AI ballooning only suggests a Keyence measurement when the part's production
 * method is mapped here, so a plan never gets a generic "Keyence" row.
 */
export default function KeyenceByProductionMethodSection() {
	const { data: rows = [], isFetching } =
		useGetKeyenceByProductionMethodQuery();
	const { data: methods = [] } = useGetProductionMethodsQuery();
	const { data: devices = [] } = useGetKeyenceSystemsQuery({
		include_unassigned: true,
	});
	const [createRow, { isLoading: isCreating }] =
		useCreateKeyenceByProductionMethodMutation();
	const [updateRow] = useUpdateKeyenceByProductionMethodMutation();
	const [deleteRow] = useDeleteKeyenceByProductionMethodMutation();

	const [newMethod, setNewMethod] = useState('');
	const [newDevice, setNewDevice] = useState<string | undefined>(undefined);

	const deviceOptions = devices.map((d) => ({ label: d.name, value: d.id }));
	const mapped = useMemo(
		() => new Set(rows.map((r) => r.production_method.trim().toLowerCase())),
		[rows]
	);
	const unmapped = methods.filter((m) => !mapped.has(m.toLowerCase()));

	const fail = (action: string, err: unknown) =>
		notification.error({
			message: `Could not ${action}`,
			description: extractApiError(err),
		});

	const handleAdd = async () => {
		if (!newMethod.trim() || !newDevice) return;
		try {
			await createRow({
				production_method: newMethod.trim(),
				keyence_device: newDevice,
			}).unwrap();
			setNewMethod('');
			setNewDevice(undefined);
		} catch (err) {
			fail('add the mapping', err);
		}
	};

	const handleDeviceChange = async (
		row: ProductionMethodKeyence,
		deviceId: string
	) => {
		try {
			await updateRow({ id: row.id, keyence_device: deviceId }).unwrap();
		} catch (err) {
			fail('change the device', err);
		}
	};

	const handleDelete = async (row: ProductionMethodKeyence) => {
		try {
			await deleteRow({ id: row.id }).unwrap();
		} catch (err) {
			fail('remove the mapping', err);
		}
	};

	return (
		<section
			aria-labelledby="keyence-by-production-method-title"
			data-testid="keyence-by-production-method"
		>
			<Title
				level={5}
				id="keyence-by-production-method-title"
				style={{ marginTop: 0 }}
			>
				Keyence device by production method
			</Title>
			<Paragraph type="secondary">
				AI ballooning measures a dimension on the Keyence only when the
				part&apos;s production method has a device here. Parts with an unmapped
				method get Hand Gage instead, so a plan never carries a generic
				&quot;Keyence&quot; measurement.
			</Paragraph>

			{unmapped.length > 0 && (
				<Alert
					type="warning"
					showIcon
					style={{ marginBottom: 12 }}
					message={`No Keyence device for: ${unmapped.join(', ')}`}
				/>
			)}

			<Table<ProductionMethodKeyence>
				rowKey="id"
				size="small"
				loading={isFetching}
				pagination={false}
				dataSource={rows}
				locale={{ emptyText: 'No production methods mapped yet.' }}
				columns={[
					{
						title: 'Production method',
						dataIndex: 'production_method',
						width: '40%',
					},
					{
						title: 'Keyence device',
						dataIndex: 'keyence_device',
						render: (_value, row) => (
							<Select
								size="small"
								style={{ width: '100%' }}
								value={row.keyence_device}
								options={deviceOptions}
								onChange={(deviceId) => handleDeviceChange(row, deviceId)}
								aria-label={`Keyence device for ${row.production_method}`}
							/>
						),
					},
					{
						key: 'actions',
						width: 48,
						render: (_value, row) => (
							<Popconfirm
								title={`Remove the device for ${row.production_method}?`}
								okText="Remove"
								okButtonProps={{ danger: true }}
								onConfirm={() => handleDelete(row)}
							>
								<Button
									size="small"
									type="text"
									danger
									icon={<DeleteOutlined />}
									aria-label={`Remove ${row.production_method}`}
								/>
							</Popconfirm>
						),
					},
				]}
			/>

			<Flex gap={8} style={{ marginTop: 12 }}>
				<AutoComplete
					style={{ flex: 2 }}
					value={newMethod}
					onChange={setNewMethod}
					options={unmapped.map((m) => ({ value: m }))}
					filterOption={(input, option) =>
						String(option?.value ?? '')
							.toLowerCase()
							.includes(input.toLowerCase())
					}
					placeholder="Production method, e.g. Fastlane"
					aria-label="Production method"
				/>
				<Select
					style={{ flex: 2 }}
					value={newDevice}
					onChange={setNewDevice}
					options={deviceOptions}
					placeholder="Keyence device"
					aria-label="Keyence device"
				/>
				<Button
					type="primary"
					icon={<PlusOutlined />}
					onClick={handleAdd}
					loading={isCreating}
					disabled={!newMethod.trim() || !newDevice}
				>
					Add
				</Button>
			</Flex>
		</section>
	);
}
